import { Injectable } from '@angular/core';
import {
	HttpContextToken,
	HttpErrorResponse,
	HttpEvent,
	HttpHandler,
	HttpInterceptor,
	HttpRequest,
	HttpStatusCode
} from '@angular/common/http';
import { Observable, switchMap, throwError, combineLatest } from 'rxjs';
import { catchError, filter, map, take, tap } from 'rxjs/operators';
import { AuthInfoService } from '@shared/services/auth-info.service';
import { ShellQuery } from '@shared/store/shell/shell-query';
import { AuthInfoQuery } from '@shared/store/authInfo/auth-info-query';
import { AuthInfoStore } from '@shared/store/authInfo/auth-info-store';
import { JWTStateEnum } from '@shared/models/jwt-state.enum';
import { LoggerService } from '@core/services/logger/logger.service';
import { Maybe } from '@/types/globals';
import { enumEquals } from '@shared/utils/enum-utils';
import { HostPlatformService } from '@shared/services/host-platform.service';

export const SKIP_JWT_ADDING = new HttpContextToken(() => false);

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
	constructor(
		private readonly authInfoService: AuthInfoService,
		private readonly shellQuery: ShellQuery,
		private readonly authInfoQuery: AuthInfoQuery,
		private readonly authInfoStore: AuthInfoStore,
		private readonly logger: LoggerService,
		private readonly hostPlatformService: HostPlatformService
	) {}

	intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
		if (this.isBearerAuthenticationSupported(req) && !this.isScannerEndpointOnScanner(req)) {
			return this.makeRequestWithJWT(req, next);
		} else {
			return this.makeRequestWithoutJWT(req, next);
		}
	}

	private isBearerAuthenticationSupported(req: HttpRequest<unknown>): boolean {
		return (
			this.authInfoService.isJWTSupported &&
			!(req.url.startsWith(this.shellQuery.staticFilesEndpoint) || req.context.get(SKIP_JWT_ADDING))
		);
	}

	private isScannerEndpointOnScanner(req: HttpRequest<unknown>): boolean {
		const { apiEndpoint } = this.shellQuery;

		return !!apiEndpoint && req.url.startsWith(apiEndpoint) && this.hostPlatformService.isScanner;
	}

	private makeRequestWithoutJWT(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
		return next.handle(req).pipe(
			map((event: HttpEvent<unknown>) => {
				return event;
			}),
			catchError((error: HttpErrorResponse) => {
				if (enumEquals(error.status, HttpStatusCode.Unauthorized)) {
					this.authInfoService.backToLogin();
				}

				return throwError(() => error);
			})
		);
	}

	private makeRequestWithJWT(req: HttpRequest<unknown>, next: HttpHandler, counter: number = 0): Observable<HttpEvent<unknown>> {
		switch (this.authInfoQuery.jwtState) {
			case JWTStateEnum.CannotBeRefreshed:
				return throwError(() => new Error(`The request with url: ${req.url} was canceled because token cannot be refreshed`));
			case JWTStateEnum.InProcessOfRefresh:
				return this.makeRequestWhenRefreshIsDone(req, next, ++counter);
			default: {
				const token = this.authInfoQuery.jwt;

				if (!token) {
					this.logger.warn(`The request with url: ${req.url} will be called without token because it is missing`, {
						module: 'AuthInterceptor'
					});
				}

				const clonedRequest = req.clone({
					headers: req.headers.set('Authorization', `Bearer ${token}`).set('X-Company-Id', String(this.shellQuery.companyId))
				});

				return next.handle(clonedRequest).pipe(
					catchError((error: HttpErrorResponse) => {
						if (enumEquals(error.status, HttpStatusCode.Unauthorized) && counter < 3) {
							return this.handleUnauthorizedError(req, next, token, ++counter);
						}

						return throwError(() => error);
					})
				);
			}
		}
	}

	private makeRequestWhenRefreshIsDone(req: HttpRequest<unknown>, next: HttpHandler, counter: number): Observable<HttpEvent<unknown>> {
		return this.authInfoQuery.jwtState$.pipe(
			filter(jwtState => jwtState !== JWTStateEnum.InProcessOfRefresh),
			take(1),
			switchMap(() => this.makeRequestWithJWT(req, next, counter))
		);
	}

	private refreshAuthInfo() {
		return this.authInfoService.refreshAuthInfo().pipe(
			take(1),
			tap(() => {
				this.authInfoStore.update({ JWTState: JWTStateEnum.Ready });
			}),
			catchError((e: HttpErrorResponse) => {
				this.authInfoStore.update({ JWTState: JWTStateEnum.CannotBeRefreshed });

				return throwError(() => e);
			})
		);
	}

	private handleUnauthorizedError(
		req: HttpRequest<unknown>,
		next: HttpHandler,
		token: Maybe<string>,
		counter: number
	): Observable<HttpEvent<unknown>> {
		if (this.authInfoQuery.jwtState === JWTStateEnum.InProcessOfRefresh) {
			return this.makeRequestWhenRefreshIsDone(req, next, counter);
		}

		if (this.authInfoQuery.jwtState === JWTStateEnum.Ready) {
			const hasTokenAlreadyUpdated = token !== this.authInfoQuery.jwt;

			if (hasTokenAlreadyUpdated) {
				return this.makeRequestWhenRefreshIsDone(req, next, counter);
			}
			this.authInfoStore.update({ JWTState: JWTStateEnum.InProcessOfRefresh });

			const refresh$ = this.refreshAuthInfo();
			const request$ = this.makeRequestWhenRefreshIsDone(req, next, counter);

			return combineLatest([request$, refresh$]).pipe(map(([event]) => event));
		}

		return throwError(() => new Error(`The request with url: ${req.url} was canceled due to token cannot be refreshed`));
	}
}
